"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import ScrollAnimate from "./ScrollAnimate";

type UnitMode = "block" | "acre";

// Trial data from 2024 BC field trial
const BC_TRIAL_2024 = {
  ACRES_TOTAL: 40,
  WATER_USE_REDUCTION_PERCENT: 24,
  PUMP_HOURS_REDUCTION: 131,
} as const;

// Economic value assumptions (per acre per year)
const PULSELOGIC_ECONOMICS = {
  NET_GAIN_LOW: 520,
  NET_GAIN_HIGH: 900,
} as const;

type ProofIconName = "gain" | "water" | "runtime";

function ProofIcon({ name }: { name: ProofIconName }) {
  const commonProps = {
    width: 32,
    height: 32,
    viewBox: "0 0 48 48",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  if (name === "gain") {
    return (
      <svg {...commonProps}>
        <rect x="6" y="7" width="36" height="34" rx="7" />
        <path d="M13 32l8-8 6 5 9-11" />
        <path d="M29 18h7v7" />
        <path d="M13 36h23" opacity="0.45" />
      </svg>
    );
  }

  if (name === "water") {
    return (
      <svg {...commonProps}>
        <path d="M24 5S12 19 12 28a12 12 0 0 0 24 0C36 19 24 5 24 5Z" />
        <path d="M18 29c2 2 4 3 6 3s4-1 6-3" />
        <path d="M19 36c1.5.8 3.2 1.2 5 1.2" opacity="0.5" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M20 5h8M24 5v5M35.5 12.5 39 9" />
      <circle cx="24" cy="27" r="14" />
      <path d="M24 17v10l7 4" />
      <path d="M16 27h8" opacity="0.45" />
    </svg>
  );
}

function MetricSubtitle({ children, compact = false }: { children: string; compact?: boolean }) {
  return (
    <p
      className={`whitespace-nowrap text-slate-600 dark:text-slate-400 ${
        compact ? "text-xs" : "text-sm"
      }`}
    >
      {children}
    </p>
  );
}

export default function ProofSection() {
  const [unitMode, setUnitMode] = useState<UnitMode>("block");

  const cad = useMemo(
    () =>
      new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    [],
  );

  const pumpHoursDisplay = useMemo(() => {
    if (unitMode === "block") {
      return {
        value: `${BC_TRIAL_2024.PUMP_HOURS_REDUCTION} hours`,
        subtitle: `per year • per ${BC_TRIAL_2024.ACRES_TOTAL}-acre field`,
      };
    }

    const perAcre = BC_TRIAL_2024.PUMP_HOURS_REDUCTION / BC_TRIAL_2024.ACRES_TOTAL;
    const rounded = perAcre < 10 ? perAcre.toFixed(1) : Math.round(perAcre).toString();

    return {
      value: `${rounded} hours`,
      subtitle: "per year • per acre",
    };
  }, [unitMode]);

  const netGainDisplay = useMemo(() => {
    const formatRange = (low: number) => `${cad.format(low)}`;

    if (unitMode === "block") {
      return {
        value: formatRange(PULSELOGIC_ECONOMICS.NET_GAIN_LOW * BC_TRIAL_2024.ACRES_TOTAL),
        subtitle: `per year • per ${BC_TRIAL_2024.ACRES_TOTAL}-acre field`,
      };
    }

    return {
      value: formatRange(PULSELOGIC_ECONOMICS.NET_GAIN_LOW),
      subtitle: "per year • per acre",
    };
  }, [cad, unitMode]);

  return (
    <section
      id="proof"
      className="border-y border-[#c5d5cd] bg-[#dce8e2] py-12 dark:border-white/10 dark:bg-[#18241f] md:py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimate>
          <div className="mb-6 text-center md:mb-8">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4 dark:text-slate-100">
              Real results. Measured.
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed dark:text-slate-400">
              Compared to irrigating each zone the same amount, Ensemble&apos;s zone-by-zone
              control reduces inputs and pump runtime.
            </p>
          </div>
        </ScrollAnimate>

        <div className="max-w-4xl mx-auto">
          <ScrollAnimate delay={100}>
            <div className="mb-4 sm:mb-8 grid sm:items-center">
              <div className="flex justify-center sm:mb-0">
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/70 border border-slate-200 shadow-sm text-center dark:bg-[#1f2b27] dark:border-slate-800">
                  <span className="text-slate-800 font-semibold dark:text-slate-200">
                    Results from {BC_TRIAL_2024.ACRES_TOTAL} acre blueberry field with six zones in BC
                  </span>
                </div>
              </div>
            </div>
          </ScrollAnimate>

          <ScrollAnimate delay={100}>
            <div className="mb-4 sm:mb-8 grid sm:items-center">
              <div className="flex items-center justify-center md:justify-end gap-3">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Per field</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={unitMode === "acre"}
                  onClick={() => setUnitMode((m) => (m === "block" ? "acre" : "block"))}
                  className={[
                    "relative inline-flex h-7 w-12 items-center rounded-full border transition-colors z-10",
                    unitMode === "acre"
                      ? "bg-[#257676] border-[#257676]"
                      : "bg-white border-slate-300 dark:bg-[#1f2b27] dark:border-slate-800",
                  ].join(" ")}
                >
                  <span className="sr-only">Toggle per acre metrics</span>
                  <span
                    className={[
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                      unitMode === "acre" ? "translate-x-6" : "translate-x-1",
                    ].join(" ")}
                  />
                </button>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Per acre</span>
              </div>
            </div>
          </ScrollAnimate>

          <div className="grid gap-2 md:gap-4 md:grid-cols-2 lg:grid-cols-3 items-stretch">

            <ScrollAnimate delay={150}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-[#1f2b27]">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border border-[#257676]/15 bg-[#257676]/10 text-[#257676] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] dark:text-[#62c9c9]">
                    <ProofIcon name="gain" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Estimated net gain</p>
                    <p className="mt-1 text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {netGainDisplay.value}
                    </p>
                    <MetricSubtitle compact={unitMode === "block"}>{netGainDisplay.subtitle}</MetricSubtitle>
                  </div>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed dark:text-slate-300">
                  Combines added revenue from improved yield + cost savings from labour and pumping and maintenance.
                </p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate delay={200}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-[#1f2b27]">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border border-[#3c8fb4]/15 bg-[#3c8fb4]/10 text-[#2f7897] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] dark:text-[#7dc4df]">
                    <ProofIcon name="water" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Reduce water use by</p>
                    <p className="mt-1 text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {BC_TRIAL_2024.WATER_USE_REDUCTION_PERCENT}%
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">per year</p>
                  </div>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed dark:text-slate-300">
                  Less overwatering while still meeting crop needs across fields.
                </p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate delay={250}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-[#1f2b27]">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border border-[#eca24c]/20 bg-[#eca24c]/12 text-[#8a5b20] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] dark:text-[#f3bd75]">
                    <ProofIcon name="runtime" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Pump runtime saved</p>
                    <p className="mt-1 text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {pumpHoursDisplay.value}
                    </p>
                    <MetricSubtitle compact={unitMode === "block"}>{pumpHoursDisplay.subtitle}</MetricSubtitle>
                  </div>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed dark:text-slate-300">
                  Fewer run hours means lower electricity demand and less mechanical wear.
                </p>
              </div>
            </ScrollAnimate>
          </div>
        </div>

        <ScrollAnimate delay={300}>
          <p className="mt-7 max-w-3xl mx-auto text-xs leading-snug text-slate-600 dark:text-slate-400">
            Based on a 2024 field trial in BC, Canada on a {BC_TRIAL_2024.ACRES_TOTAL}-acre field with autonomous, smart
            irrigation on a zone-by-zone basis.{" "}
            <Link href="/contact" className="text-[#257676] hover:text-[#1a5a5a] underline dark:text-[#4dbdbd] dark:hover:text-[#4dbdbd]">
              Contact us for more information
            </Link>
            .
          </p>
        </ScrollAnimate>
      </div>
    </section>
  );
}
